import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery, {
  IBaseQueryResponse
} from "../../../utils/customBaseQuery";
import { gql } from "graphql-request";
import {
  ArtifactType,
  SearchCatalogArgs
} from "../../../store/slices/query-args/searchCatalog";
import { UpcomingEvent as GlobalEvent } from "../../landing-page/slices/upcomingEventType";
import { domainTypes } from "../../../assets/config/enum-codes.json";

const searchResult = `{
  timestamp
  error
  version
  data {
    totalCount
    pageSize
    pageNumber
    responseFilters{
      skills
      searchTags
      artifactType
      difficultyLevel
      }
      dym
    searchResult {
      createdby
      id
      title
      externalownerlist
      updatedby
      branding {
        promo
        thumbnail
        banner
      }
      language
      stakeholder {
        owner
        observer
        nominationApprover
        reviewer
      }
      shortdescription
      createdat
      status
      episodeList
      publishedat
      sourcename
      sourcetype
      artifacttype
      skills {
        type
        skillid
        parentskill
        preferredskillname {
          en_UK
          en_US
        }
      }
      searchtags {
        defaultTag
        eventRegion
      }
      joblevel
      privateusergroup
      startdate
      proficiency
      mode
      function
      isblocking
      publishedby
      maxcapacity
      visibility
      registrationcost {
        amount
        currency
      }
      templateid
      enddate
      episodenumber
      episodecount
      createdzoneid
      zoneid
      nominationenddate
      role
      registrationtype
      updatedzoneid
      nominationeligibilty {
        role
        function
        jobLevel
        proficiency
      }
      mincapacity
      socialinteractions {
        contentId
        contentType
        interactionCriteria 
        trigger
      }
      clonedid
      cohortid
      org
      nominationstartdate
      location
      module
      {
      id
      }
      contentlist
                  {
                  title
                  }
      section
      {
      id
      }
      config {
        reminderConfig {
          duration
          trigger
        }
      }
      updatedat
      waitlistpercent
      eventtype
      owners {
        id
        email
      }
      duration
      createdbyid
      updatedbyid
      session
      targetaudience
      difficultylevel
      learningobjective
      catalogclassification
      launchdate
      expirydate
      pageno
      launchtype
      feedback
      ratings {
        rating
        allow_ratings
        id
      }
      artifactid
      artifacturl
      weblink
      brandingurls {
        thumbnail 
      }
       pkid
      cataloged
    starttime
    endtime
    tncdata {
      tncMap
    }
    aievalreqd
    freezeflag
    tncenabled
    coolofftime
    showresults
    timerschema {
      questionTimer
      sectionCoolOffPeriod
      sectionTimer
    }
    autocomplete
    captureimage
    passwordhash
    scheduletype
    timerenabled
    totalmarks
    allowedcodinglanguages
    totalquestions
    
    markingschema {
      partialMarking
      negativeMarking
      partialMarkSchema
      negativeMarkSchema
    }
    randomization {
      options
      section
      question
    }
    showviolation
    versionnumber
    accesscriteria
    classification
    passpercentage
    questionconfig {
      showHint
      showWatermark
      showExplanation
      showQuestionMarks
      showQuestionTitle
      codingQuestionSubmission
    }
    feedbackallowed
    questionpaperid
    videoproctoring
    assessmentpattern
    browserproctoring
    certificateconfig {
      enableCerti
      specialization
    }
    maxallowedattempts
    maxviolationaction
    maxviolationallowed {
      videoViolationCount
      browserViolationCount
    }
    scheduleinstruction
    allowedafterloginwindow
    questionpapersnapshotat
    }
  }
}`;

const searchSuggestionQuery = `{
  timestamp
  error
  version
  data {
    totalCount
    pageSize
    pageNumber
    searchResult {
      id
      title
      branding {
        promo
        thumbnail
        banner
      }
      sourcename
      sourcetype
      artifacttype
      brandingurls {
        thumbnail 
      }
      eventtype
    }
  }
}`;

export interface searchList {
  pageNumber: number;
  pageSize: number;
  searchResult: GlobalEvent[];
  totalCount: number;
  responseFilters: {
    artifactType: ArtifactType[];
    difficultyLevel: string[];
    skills: string[];
    searchTags: string[];
  };
  dym: string[];
}

export interface ExtendedSearchCatalogArgs extends SearchCatalogArgs {
  contentDomainData?: any;
}

export const GlobalSearchAPI = createApi({
  reducerPath: "globalSearchSlice",
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getGlobalSearch: builder.query<
      { globalSearch?: IBaseQueryResponse<searchList> },
      ExtendedSearchCatalogArgs
    >({
      query: ({ contentDomainData, ...searchCatalogArgs }) => {
        const artifactTypes = Array.isArray(
          searchCatalogArgs?.requestDto?.filters?.artifactType
        )
          ? searchCatalogArgs.requestDto.filters.artifactType
          : [];
        const hasAssessment = artifactTypes.includes("assessment");

        const resolvedIndices =
          hasAssessment &&
          !searchCatalogArgs?.requestDto?.indices?.includes(
            "assessment-schedule"
          )
            ? [
                ...(searchCatalogArgs?.requestDto?.indices ?? []),
                "assessment-schedule"
              ]
            : searchCatalogArgs?.requestDto?.indices;

        const resolvedArtifactType = hasAssessment
          ? artifactTypes.map((t) =>
              t === "assessment" ? "assessment_schedule" : t
            )
          : searchCatalogArgs?.requestDto?.filters?.artifactType;

        return {
          url: "/graphql",
          body: gql`query GlobalSearch (
            $filters: GlobalSearchRequest!
            $pageNumber: Int!
            $pageSize: Int!
            $sortKey: String!
            $sortOrder: String!
            $relevance: Boolean!
        
        ) {
          globalSearch(
            requestDto: $filters,
            pageNumber: $pageNumber
            pageSize: $pageSize
            sortKey: $sortKey
            sortOrder: $sortOrder
            relevance: $relevance
          ) 

        ${searchResult}
        }`,
          params: {
            variables: {
              filters: {
                ...searchCatalogArgs?.requestDto,
                indices: resolvedIndices,
                filters: {
                  ...searchCatalogArgs?.requestDto?.filters,
                  artifactType: resolvedArtifactType,
                  eventTypeList: (() => {
                    const hasNonEventArtifacts = artifactTypes.some(
                      (type) => type !== "event"
                    );
                    return hasNonEventArtifacts
                      ? undefined
                      : searchCatalogArgs?.requestDto?.filters?.eventTypeList;
                  })(),
                  skills: searchCatalogArgs?.requestDto?.filters?.skills
                    ?.map((v) => v.split(", "))
                    .flat(),
                  searchTags:
                    searchCatalogArgs?.requestDto?.filters?.searchTags ||
                    searchCatalogArgs?.requestDto?.filters?.domain ||
                    searchCatalogArgs?.requestDto?.filters?.contentCategory
                      ? {
                          defaultTag:
                            (!searchCatalogArgs?.requestDto?.filters?.searchTags
                              ?.eventRegion
                              ? searchCatalogArgs?.requestDto?.filters
                                  ?.searchTags
                              : []) ?? [],
                          contentDomain: searchCatalogArgs?.requestDto?.filters
                            ?.domain
                            ? [
                                contentDomainData[
                                  searchCatalogArgs?.requestDto?.filters?.domain
                                ]
                              ]
                            : [],
                          eventRegion:
                            searchCatalogArgs?.requestDto?.filters?.searchTags
                              ?.eventRegion ?? [],
                          contentCategory: searchCatalogArgs?.requestDto
                            ?.filters?.contentCategory
                            ? Array.isArray(
                                searchCatalogArgs.requestDto.filters
                                  .contentCategory
                              )
                              ? searchCatalogArgs.requestDto.filters
                                  .contentCategory
                              : [
                                  searchCatalogArgs.requestDto.filters
                                    .contentCategory
                                ]
                            : []
                        }
                      : undefined,
                  domain: undefined,
                  contentCategory: undefined
                }
              },
              pageNumber: searchCatalogArgs?.pageNumber,
              pageSize: searchCatalogArgs?.pageSize,
              sortKey: searchCatalogArgs?.sortKey,
              sortOrder: searchCatalogArgs?.sortOrder,
              relevance: searchCatalogArgs?.relevance
            }
          }
        };
      },
      extraOptions: { service: "Catalog" },
      transformResponse: (
        baseQueryReturnValue: { globalSearch?: IBaseQueryResponse<searchList> },
        meta,
        arg
      ) => {
        const searchResult = arg?.requestDto?.filters?.artifactType?.includes(
          "event"
        )
          ? baseQueryReturnValue?.globalSearch?.data?.searchResult.sort(
              (globalA: any, globalB: any) =>
                Number(new Date(globalA.startdate)) -
                Number(new Date(globalB.startdate))
            )
          : baseQueryReturnValue?.globalSearch?.data?.searchResult;

        const newQueryResult = {
          ...baseQueryReturnValue,
          globalSearch: {
            ...baseQueryReturnValue?.globalSearch,
            data: {
              ...baseQueryReturnValue?.globalSearch?.data,
              searchResult
            }
          }
        };
        return newQueryResult as any;
      },
      merge(currentCacheData, responseData, otherArgs) {
        const newMap = new Map();
        currentCacheData?.globalSearch?.data?.searchResult?.forEach(
          ({ id, ...rest }) => {
            if (!newMap.has(String(id))) {
              newMap.set(String(id), { ...rest, id });
            }
          }
        );
        responseData?.globalSearch?.data?.searchResult?.forEach(
          ({ id, ...rest }) => {
            if (!newMap.has(String(id))) {
              newMap.set(String(id), { ...rest, id });
            }
          }
        );
        if (
          otherArgs?.arg?.pageNumber > 1 &&
          Array.isArray(currentCacheData?.globalSearch?.data?.searchResult) &&
          Array.isArray(responseData?.globalSearch?.data?.searchResult)
        ) {
          let searchResult = [...newMap.values()];
          if (
            otherArgs?.arg?.requestDto?.filters?.startDate ||
            otherArgs?.arg?.requestDto?.filters?.nominationStartDate
          ) {
            if (
              otherArgs?.arg?.requestDto?.filters?.startDate ||
              otherArgs?.arg?.requestDto?.filters?.artifactType?.includes(
                "event"
              )
            ) {
              searchResult = searchResult.sort(
                (globalA, globalB) =>
                  Number(globalA.startdate) - Number(globalB.startdate)
              );
            }
          }
          return {
            ...responseData,
            globalSearch: {
              ...responseData?.globalSearch,
              data: {
                ...responseData?.globalSearch?.data,
                searchResult
              }
            }
          };
        } else {
          return responseData;
        }
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { pageNumber, ...rest } = queryArgs as any;
        return `${endpointName}-${JSON.stringify(rest)}`;
      }
    }),
    getSearchSuggestion: builder.query<
      { globalSearch?: IBaseQueryResponse<searchList> },
      ExtendedSearchCatalogArgs
    >({
      query: ({ contentDomainData, ...searchCatalogArgs }) => ({
        url: "/graphql",
        body: gql`query GlobalSearch (
            $filters: GlobalSearchRequest!
            $pageNumber: Int!
            $pageSize: Int!
            $sortKey: String!
            $sortOrder: String!
            $relevance: Boolean!
        
        ) {
          globalSearch(
            requestDto: $filters,
            pageNumber: $pageNumber
            pageSize: $pageSize
            sortKey: $sortKey
            sortOrder: $sortOrder
            relevance: $relevance
          ) 

        ${searchSuggestionQuery}
        }`,
        params: {
          variables: {
            filters: {
              ...searchCatalogArgs?.requestDto,
              indices: searchCatalogArgs?.requestDto?.indices,
              filters: {
                ...searchCatalogArgs?.requestDto?.filters,
                skills: searchCatalogArgs?.requestDto?.filters?.skills
                  ?.map((v) => v.split(", "))
                  .flat(),
                searchTags:
                  searchCatalogArgs?.requestDto?.filters?.searchTags ||
                  searchCatalogArgs?.requestDto?.filters?.domain ||
                  searchCatalogArgs?.requestDto?.filters?.contentCategory
                    ? {
                        defaultTag:
                          (!searchCatalogArgs?.requestDto?.filters?.searchTags
                            ?.eventRegion
                            ? searchCatalogArgs?.requestDto?.filters?.searchTags
                            : []) ?? [],
                        contentDomain: searchCatalogArgs?.requestDto?.filters
                          ?.domain
                          ? [
                              contentDomainData[
                                searchCatalogArgs?.requestDto?.filters?.domain
                              ]
                            ]
                          : [],
                        eventRegion:
                          searchCatalogArgs?.requestDto?.filters?.searchTags
                            ?.eventRegion ?? [],
                        contentCategory: searchCatalogArgs?.requestDto?.filters
                          ?.contentCategory
                          ? Array.isArray(
                              searchCatalogArgs.requestDto.filters
                                .contentCategory
                            )
                            ? searchCatalogArgs.requestDto.filters
                                .contentCategory
                            : [
                                searchCatalogArgs.requestDto.filters
                                  .contentCategory
                              ]
                          : []
                      }
                    : undefined,
                domain: undefined,
                contentCategory: undefined
              }
            },
            pageNumber: 1,
            pageSize: 3,
            sortKey: searchCatalogArgs?.sortKey,
            sortOrder: searchCatalogArgs?.sortOrder,
            relevance: searchCatalogArgs?.relevance
          }
        }
      }),
      extraOptions: { service: "Catalog" },
      transformResponse: (
        baseQueryReturnValue: { globalSearch?: IBaseQueryResponse<searchList> },
        meta,
        arg
      ) => {
        const searchResult = arg?.requestDto?.filters?.artifactType?.includes(
          "event"
        )
          ? baseQueryReturnValue?.globalSearch?.data?.searchResult.sort(
              (globalA: any, globalB: any) =>
                Number(new Date(globalA.startdate)) -
                Number(new Date(globalB.startdate))
            )
          : baseQueryReturnValue?.globalSearch?.data?.searchResult;

        const newQueryResult = {
          ...baseQueryReturnValue,
          globalSearch: {
            ...baseQueryReturnValue?.globalSearch,
            data: {
              ...baseQueryReturnValue?.globalSearch?.data,
              searchResult
            }
          }
        };
        return newQueryResult as any;
      },
      merge(currentCacheData, responseData, otherArgs) {
        const newMap = new Map();
        currentCacheData?.globalSearch?.data?.searchResult?.forEach(
          ({ id, ...rest }) => {
            if (!newMap.has(String(id))) {
              newMap.set(String(id), { ...rest, id });
            }
          }
        );
        responseData?.globalSearch?.data?.searchResult?.forEach(
          ({ id, ...rest }) => {
            if (!newMap.has(String(id))) {
              newMap.set(String(id), { ...rest, id });
            }
          }
        );
        if (
          otherArgs?.arg?.pageNumber > 1 &&
          Array.isArray(currentCacheData?.globalSearch?.data?.searchResult) &&
          Array.isArray(responseData?.globalSearch?.data?.searchResult)
        ) {
          let searchResult = [...newMap.values()];
          if (
            otherArgs?.arg?.requestDto?.filters?.startDate ||
            otherArgs?.arg?.requestDto?.filters?.nominationStartDate
          ) {
            if (
              otherArgs?.arg?.requestDto?.filters?.startDate ||
              otherArgs?.arg?.requestDto?.filters?.artifactType?.includes(
                "event"
              )
            ) {
              searchResult = searchResult.sort(
                (globalA, globalB) =>
                  Number(globalA.startdate) - Number(globalB.startdate)
              );
            }
          }
          return {
            ...responseData,
            globalSearch: {
              ...responseData?.globalSearch,
              data: {
                ...responseData?.globalSearch?.data,
                searchResult
              }
            }
          };
        } else {
          return responseData;
        }
      },
      serializeQueryArgs: (serachCatalogArgs) => {
        const { pageNumber, ...rest } = serachCatalogArgs.queryArgs;
        return String(JSON.stringify(rest));
      }
    }),
    getAiSmartSearch: builder.mutation({
      query: (data: any) => {
        const contentData = {
          searchQuery: data
        };
        return {
          url: `/proxy/smartSearch`,
          headers: {
            service: "aiSmartSearch"
          },
          method: "POST",
          body: contentData
        };
      },
      extraOptions: { service: "Strapi" }
    })
  })
});

export const {
  useGetGlobalSearchQuery,
  useLazyGetGlobalSearchQuery,
  useLazyGetSearchSuggestionQuery,
  useGetSearchSuggestionQuery,
  useGetAiSmartSearchMutation
} = GlobalSearchAPI;

export default GlobalSearchAPI;
